import { Injectable, signal, computed, inject, effect, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { LoggerService } from './logger.service';
import { UserActivityService } from './user-activity.service';

/**
 * Configuration for session timeout behavior
 */
export interface SessionTimeoutConfig {
  /** Total idle time before automatic logout (milliseconds) */
  sessionTimeoutMs: number;
  /** Show warning this many milliseconds before logout */
  warningBeforeMs: number;
  /** Grace period after warning shows (milliseconds) */
  gracePeriodMs: number;
  /** Enable/disable timeout monitoring */
  enabled: boolean;
}

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: SessionTimeoutConfig = {
  sessionTimeoutMs: 30 * 60 * 1000, // 30 minutes
  warningBeforeMs: 5 * 60 * 1000,   // 5 minutes before logout
  gracePeriodMs: 60 * 1000,          // 60 seconds grace period
  enabled: true
};

/**
 * Session Timeout Service (DEV-406)
 *
 * Features:
 * - Monitors user activity (mouse, keyboard, touch events)
 * - Shows warning dialog before automatic logout
 * - Provides countdown timer during grace period
 * - Allows user to extend session or logout explicitly
 * - Integrates with AuthService for token refresh and logout
 * - Uses signals for reactive state management
 * - Supports configurable timeout durations
 *
 * Usage:
 * ```typescript
 * constructor(private sessionTimeout: SessionTimeoutService) {
 *   // Subscribe to warning dialog trigger
 *   this.sessionTimeout.showWarning$.subscribe(() => {
 *     // Show warning dialog component
 *   });
 *
 *   // Subscribe to session expired event
 *   this.sessionTimeout.sessionExpired$.subscribe(() => {
 *     // Handle forced logout
 *   });
 * }
 * ```
 *
 * Configuration:
 * ```typescript
 * // Update config at runtime
 * this.sessionTimeout.updateConfig({
 *   sessionTimeoutMs: 20 * 60 * 1000, // 20 minutes
 *   warningBeforeMs: 2 * 60 * 1000,   // 2 minutes warning
 * });
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class SessionTimeoutService {
  private readonly authService = inject(AuthService);
  private readonly logger = inject(LoggerService);
  private readonly userActivityService = inject(UserActivityService);
  private readonly destroyRef = inject(DestroyRef);

  // Timeout management
  private lastActivityTime: number = Date.now();
  private warningTimer?: ReturnType<typeof setTimeout>;
  private logoutTimer?: ReturnType<typeof setTimeout>;
  private countdownInterval?: ReturnType<typeof setInterval>;

  // Configuration
  private readonly _config = signal<SessionTimeoutConfig>(DEFAULT_CONFIG);
  public readonly config = this._config.asReadonly();

  // State signals
  private readonly _isWarningVisible = signal<boolean>(false);
  private readonly _remainingSeconds = signal<number>(0);
  private readonly _sessionExpiresAt = signal<Date | null>(null);
  private readonly _isMonitoring = signal<boolean>(false);

  // Public readonly signals
  public readonly isWarningVisible = this._isWarningVisible.asReadonly();
  public readonly remainingSeconds = this._remainingSeconds.asReadonly();
  public readonly sessionExpiresAt = this._sessionExpiresAt.asReadonly();
  public readonly isMonitoring = this._isMonitoring.asReadonly();

  // Event subjects
  private readonly showWarningSubject = new Subject<void>();
  private readonly sessionExpiredSubject = new Subject<void>();

  // Public observables
  public readonly showWarning$: Observable<void> = this.showWarningSubject.asObservable();
  public readonly sessionExpired$: Observable<void> = this.sessionExpiredSubject.asObservable();

  // Computed values
  public readonly timeUntilWarning = computed(() => {
    const expiresAt = this._sessionExpiresAt();
    if (!expiresAt) {return 0;}

    const now = Date.now();
    const expiresAtMs = expiresAt.getTime();
    const warningMs = this._config().warningBeforeMs;
    const timeUntil = expiresAtMs - warningMs - now;

    return Math.max(0, timeUntil);
  });

  constructor() {
    // React to authentication changes
    effect(() => {
      const isAuthenticated = this.authService.isAuthenticated();
      const config = this._config();

      if (isAuthenticated && config.enabled) {
        this.logger.debug('[SessionTimeout] User authenticated, starting monitoring');
        this.startMonitoring();
      } else {
        this.logger.debug('[SessionTimeout] User not authenticated or disabled, stopping monitoring');
        this.stopMonitoring();
      }
    });

    this.logger.debug('[SessionTimeout] Service initialized', {
      config: this._config()
    });

    this.destroyRef.onDestroy(() => {
      this.logger.debug('[SessionTimeout] Service destroying');
      this.stopMonitoring();
    });
  }

  /**
   * Start monitoring user activity and session timeout
   */
  public startMonitoring(): void {
    if (this._isMonitoring()) {
      this.logger.debug('[SessionTimeout] Already monitoring, ignoring start request');
      return;
    }

    const config = this._config();
    if (!config.enabled) {
      this.logger.debug('[SessionTimeout] Monitoring disabled in config');
      return;
    }

    if (!this.authService.isAuthenticated()) {
      this.logger.debug('[SessionTimeout] User not authenticated, cannot start monitoring');
      return;
    }

    this.logger.info('[SessionTimeout] Starting session timeout monitoring', {
      sessionTimeoutMin: config.sessionTimeoutMs / 60000,
      warningBeforeMin: config.warningBeforeMs / 60000,
      gracePeriodSec: config.gracePeriodMs / 1000
    });

    this._isMonitoring.set(true);
    this.lastActivityTime = Date.now();
    this.updateSessionExpiry();

    // Listen to user activity events
    this.setupActivityListeners();

    // Schedule warning and logout timers
    this.scheduleTimeouts();
  }

  /**
   * Stop monitoring session timeout
   */
  public stopMonitoring(): void {
    if (!this._isMonitoring()) {
      return;
    }

    this.logger.debug('[SessionTimeout] Stopping monitoring');

    this._isMonitoring.set(false);
    this._isWarningVisible.set(false);
    this._remainingSeconds.set(0);
    this._sessionExpiresAt.set(null);

    this.clearAllTimers();
  }

  /**
   * Extend the session (user clicked "Stay logged in")
   * Refreshes authentication token and resets timeout timers
   */
  public extendSession(): void {
    this.logger.info('[SessionTimeout] User extending session');

    this._isWarningVisible.set(false);
    this.clearAllTimers();

    // Refresh the access token.
    // COLD-LOAD-RACE FIX: use the single-flight refresh so a timer-fired
    // "extend session" can never race the auth interceptor's refresh and
    // double-rotate the (rotating) refresh-token cookie into a 401.
    this.authService.refreshTokenSingleFlight().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe({
      next: (token) => {
        if (token) {
          this.logger.debug('[SessionTimeout] Token refreshed successfully');
          this.resetActivityTimer();
        } else {
          this.logger.warn('[SessionTimeout] Token refresh returned empty token');
          this.handleSessionExpired();
        }
      },
      error: (error) => {
        this.logger.error('[SessionTimeout] Token refresh failed', { error });
        this.handleSessionExpired();
      }
    });
  }

  /**
   * Logout the user (user clicked "Logout" or session expired)
   */
  public logout(): void {
    this.logger.info('[SessionTimeout] Logging out user');

    this.stopMonitoring();
    this.authService.logout();
  }

  /**
   * Update configuration
   */
  public updateConfig(config: Partial<SessionTimeoutConfig>): void {
    const currentConfig = this._config();
    const newConfig = { ...currentConfig, ...config };

    this.logger.debug('[SessionTimeout] Updating config', {
      old: currentConfig,
      new: newConfig
    });

    this._config.set(newConfig);

    // Restart monitoring if active
    if (this._isMonitoring()) {
      this.stopMonitoring();
      this.startMonitoring();
    }
  }

  /**
   * Reset activity timer (called when user activity detected)
   */
  private resetActivityTimer(): void {
    this.lastActivityTime = Date.now();
    this.updateSessionExpiry();

    // If warning is visible, hide it
    if (this._isWarningVisible()) {
      this._isWarningVisible.set(false);
      this._remainingSeconds.set(0);
      this.clearAllTimers();
    }

    // Reschedule timeouts
    this.scheduleTimeouts();
  }

  /**
   * Update the session expiry time
   */
  private updateSessionExpiry(): void {
    const config = this._config();
    const expiresAt = new Date(this.lastActivityTime + config.sessionTimeoutMs);
    this._sessionExpiresAt.set(expiresAt);

    this.logger.debug('[SessionTimeout] Session expiry updated', {
      expiresAt: expiresAt.toISOString(),
      minutesRemaining: (expiresAt.getTime() - Date.now()) / 60000
    });
  }

  /**
   * Setup activity listeners using UserActivityService
   * Reuses the centralized activity detection instead of duplicating event listeners
   */
  private setupActivityListeners(): void {
    // Start monitoring in UserActivityService if not already running
    this.userActivityService.startMonitoring();

    // Subscribe to activity events from UserActivityService
    this.userActivityService.activity$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(() => this._isMonitoring())
      )
      .subscribe(() => {
        this.logger.debug('[SessionTimeout] User activity detected via UserActivityService');
        this.resetActivityTimer();
      });
  }

  /**
   * Schedule warning and logout timers
   */
  private scheduleTimeouts(): void {
    // Idempotency guard: cancel any previously-scheduled warning/logout
    // timers before scheduling new ones. resetActivityTimer() calls this on
    // every (debounced) activity event and on extend-session success; without
    // cancelling first, each call reassigned this.warningTimer/this.logoutTimer
    // and orphaned the prior setTimeout handle, which still fired. Those orphan
    // timers each re-entered showWarning(), stacking duplicate countdown
    // intervals so the warning countdown raced to 0 — even right after the user
    // clicked "Stay Logged In".
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = undefined;
    }
    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = undefined;
    }

    const config = this._config();
    const now = Date.now();
    const warningTime = this.lastActivityTime + config.sessionTimeoutMs - config.warningBeforeMs;
    const logoutTime = this.lastActivityTime + config.sessionTimeoutMs;

    const timeUntilWarning = warningTime - now;
    const timeUntilLogout = logoutTime - now;

    this.logger.debug('[SessionTimeout] Scheduling timeouts', {
      timeUntilWarningMin: timeUntilWarning / 60000,
      timeUntilLogoutMin: timeUntilLogout / 60000
    });

    // Schedule warning
    if (timeUntilWarning > 0) {
      this.warningTimer = setTimeout(() => {
        this.showWarning();
      }, timeUntilWarning);
    } else {
      // Warning time already passed, show immediately
      this.showWarning();
    }

    // Schedule logout
    if (timeUntilLogout > 0) {
      this.logoutTimer = setTimeout(() => {
        this.handleSessionExpired();
      }, timeUntilLogout);
    } else {
      // Logout time already passed, logout immediately
      this.handleSessionExpired();
    }
  }

  /**
   * Show warning dialog and start countdown
   */
  private showWarning(): void {
    this.logger.warn('[SessionTimeout] Showing session timeout warning');

    // Defensive: guarantee a single countdown interval. If showWarning() is
    // ever entered again before the previous countdown was cleared, a second
    // setInterval would stack on the first and remainingSeconds would decrement
    // multiple times per second.
    this.clearCountdown();

    this._isWarningVisible.set(true);
    this.showWarningSubject.next();

    // Start countdown timer
    const config = this._config();
    const gracePeriodSeconds = Math.floor(config.gracePeriodMs / 1000);
    this._remainingSeconds.set(gracePeriodSeconds);

    this.countdownInterval = setInterval(() => {
      const remaining = this._remainingSeconds();
      if (remaining > 0) {
        this._remainingSeconds.set(remaining - 1);
      } else {
        this.clearCountdown();
      }
    }, 1000);
  }

  /**
   * Handle session expiration
   */
  private handleSessionExpired(): void {
    this.logger.warn('[SessionTimeout] Session expired, logging out user');

    this._isWarningVisible.set(false);
    this._remainingSeconds.set(0);

    this.sessionExpiredSubject.next();
    this.logout();
  }

  /**
   * Clear all timers
   */
  private clearAllTimers(): void {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = undefined;
    }

    if (this.logoutTimer) {
      clearTimeout(this.logoutTimer);
      this.logoutTimer = undefined;
    }

    this.clearCountdown();
  }

  /**
   * Clear countdown interval
   */
  private clearCountdown(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = undefined;
    }
  }

}
